import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion'
import { Star, LayoutGrid, MessageSquare, SquareDashedMousePointer } from 'lucide-react'
import { BrandBar } from '../components/BrandBar'
import { BucketBar } from '../components/BucketBar'
import { IdentityPrompt } from '../components/IdentityPrompt'
import { NoteSheet } from '../components/NoteSheet'
import { CommentSheet, type SheetComment } from '../components/CommentSheet'
import { AnnotationLayer } from '../components/AnnotationLayer'
import { KeyboardHint } from '../components/KeyboardHint'
import { PhotoSurface } from '../components/PhotoSurface'
import { BrandingBadge } from '../components/BrandingBadge'
import { PolicyDownloadMenu } from '../components/PolicyDownloadMenu'
import { useManifest } from '../state/manifest'
import { clientPolicyForManifest } from '../state/capabilities'
import { useSession } from '../state/session'
import { useKeyboard } from '../hooks/useKeyboard'
import type { Bucket, CommentRegion } from '../types'
import { photographerPickLabel } from '../utils/photographerName'
import { trackShareEvent, submitComment, fetchComments, type ManifestComment } from '../api/client'

const SWIPE_THRESHOLD = 100

export function Photo() {
  const { index } = useParams<{ index: string }>()
  const navigate = useNavigate()
  const manifest = useManifest((s) => s.manifest)

  const photos = manifest?.photos ?? []
  const i = Math.max(0, Math.min(photos.length - 1, parseInt(index ?? '0', 10) || 0))
  // photo id → full-array index, for the swipe-undo jump back.
  const photoIndexById = useMemo(() => {
    const map: Record<string, number> = {}
    photos.forEach((p, idx) => {
      map[p.id] = idx
    })
    return map
  }, [photos])
  const photo = photos[i]

  const picks = useSession((s) => s.picks)
  const shareToken = useSession((s) => s.shareToken)
  const setBucket = useSession((s) => s.setBucket)
  const setNote = useSession((s) => s.setNote)

  const [noteOpen, setNoteOpen] = useState(false)
  const [zoomed, setZoomed] = useState(false)
  const [commentOpen, setCommentOpen] = useState(false)
  // Optimistic local echo of comments the viewer adds this session, on top of
  // the public comments the manifest shipped for this photo.
  const [addedComments, setAddedComments] = useState<SheetComment[]>([])
  // Re-fetched public comments. The manifest only ships the comments that
  // existed at first load, so a studio reply that lands after the gallery opened
  // is otherwise invisible. We refetch when the viewer opens a photo and when
  // the tab regains focus, then merge with the manifest's + local echoes.
  const [liveComments, setLiveComments] = useState<ManifestComment[] | null>(null)
  // Markup state: the comment whose region is highlighted, the region the
  // reviewer has drawn but not yet sent, and whether draw mode is armed.
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null)
  const [pendingRegion, setPendingRegion] = useState<CommentRegion | null>(null)
  const [markupMode, setMarkupMode] = useState(false)
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)

  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const rotate = useTransform(x, [-200, 0, 200], [-8, 0, 8])
  const skipOpacity = useTransform(x, [-200, -40, 0], [1, 0.4, 0])
  const loveOpacity = useTransform(x, [0, 40, 200], [0, 0.4, 1])
  const noteOpacity = useTransform(y, [-160, -40, 0], [1, 0.4, 0])
  // Which way the card should fly off when a swipe decides it (+1 select,
  // -1 reject, 0 = neutral fade for taps/keyboard). Ref-read inside the exit
  // variant via `custom` on AnimatePresence.
  const exitDirRef = useRef(0)
  // A mis-swipe on a phone is one twitch — give the verdict a short undo
  // window instead of making the viewer hunt the photo down again.
  const [undo, setUndo] = useState<{ photoId: string; bucket: Bucket | null } | null>(null)
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const policy = manifest ? clientPolicyForManifest(manifest) : null
  const reviewEnabled = policy?.can.select ?? false
  const commentEnabled = policy?.can.comment ?? false

  useEffect(() => {
    if (!shareToken || !photo) return
    void trackShareEvent(shareToken, 'photo_opened', {
      photoId: photo.id,
      metadata: {
        index: i,
        category: photo.category,
        reviewEnabled,
      },
    })
  }, [i, photo, reviewEnabled, shareToken])

  const goNext = useCallback(() => {
    if (i < photos.length - 1) navigate(`/photo/${i + 1}`, { replace: true })
    else navigate(reviewEnabled ? '/review' : '/gallery')
  }, [i, photos.length, navigate, reviewEnabled])

  const goPrev = useCallback(() => {
    if (i > 0) navigate(`/photo/${i - 1}`, { replace: true })
  }, [i, navigate])

  const currentPick = photo ? picks[photo.id] : undefined

  // Public comments for this photo: the freshest server set (liveComments,
  // falling back to the manifest's first-load set) + any added this session
  // (optimistic). De-dupe by id so an echo that the refetch later confirms isn't
  // rendered twice. Reset the session echoes when the photo changes.
  const photoComments = useMemo<SheetComment[]>(() => {
    if (!photo) return []
    const source = liveComments ?? manifest?.comments ?? []
    const fromServer = source
      .filter((c) => c.photoId === photo.id)
      .map((c) => ({
        id: c.id,
        body: c.body,
        createdAt: c.createdAt,
        region: c.region ?? null,
        parentCommentId: c.parentCommentId ?? null,
        // The worker returns only validated public aliases here; internal voter
        // credentials never enter the gallery comment payload.
        mentionNames: c.mentions ?? [],
      }))
    const seen = new Set(fromServer.map((c) => c.id))
    const echoes = addedComments.filter((c) => !seen.has(c.id))
    return [...fromServer, ...echoes]
  }, [liveComments, manifest?.comments, photo, addedComments])

  // Refetch the link's public comments when this photo opens and whenever the
  // tab regains focus, so a studio reply made after first load shows up.
  useEffect(() => {
    if (!shareToken || !commentEnabled) return
    let cancelled = false
    const refresh = () => {
      void fetchComments(shareToken).then((cs) => {
        if (!cancelled) setLiveComments(cs)
      })
    }
    refresh()
    const onFocus = () => refresh()
    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      window.removeEventListener('focus', onFocus)
    }
  }, [commentEnabled, shareToken, photo?.id])

  useEffect(() => {
    setAddedComments([])
    setSelectedCommentId(null)
    setPendingRegion(null)
    setMarkupMode(false)
    setNaturalSize(null)
  }, [photo?.id])

  const handleSubmitComment = useCallback(
    async (body: string, parentCommentId: string | null, region: CommentRegion | null) => {
      if (!shareToken || !photo || !commentEnabled) return
      const res = await submitComment(shareToken, photo.id, body, parentCommentId, region)
      setAddedComments((prev) => [
        ...prev,
        { id: res.id, body, createdAt: res.createdAt, parentCommentId, region: res.region },
      ])
      setPendingRegion(null)
    },
    [commentEnabled, shareToken, photo]
  )

  // The reviewer finished drawing a box → carry it as the pending region and
  // open the comment sheet so they can describe it. Leave draw mode.
  const handleDrawComplete = useCallback((region: CommentRegion) => {
    setPendingRegion(region)
    setMarkupMode(false)
    setCommentOpen(true)
  }, [])

  const handleBucket = useCallback(
    (bucket: Bucket, viaSwipe = false) => {
      if (!photo || !reviewEnabled) return
      exitDirRef.current = viaSwipe ? (bucket === 'love' ? 1 : bucket === 'skip' ? -1 : 0) : 0
      // Arm the undo pill with what the verdict replaced, swipes only —
      // button taps are deliberate and already sit next to their own controls.
      if (viaSwipe) {
        if (undoTimer.current) clearTimeout(undoTimer.current)
        setUndo({ photoId: photo.id, bucket: currentPick?.bucket ?? null })
        undoTimer.current = setTimeout(() => setUndo(null), 4000)
      }
      setBucket(photo.id, bucket)
      setTimeout(goNext, 120)
    },
    [photo, reviewEnabled, setBucket, goNext, currentPick]
  )

  const onDragEnd = useCallback(
    (
      _: unknown,
      info: { offset: { x: number; y: number }; velocity: { x: number; y: number } }
    ) => {
      if (!reviewEnabled) return
      // Distance OR a committed flick — a fast short swipe is still a verdict
      // (distance-only thresholds make quick flicks feel dead).
      const decideRight = info.offset.x > SWIPE_THRESHOLD || info.velocity.x > 600
      const decideLeft = info.offset.x < -SWIPE_THRESHOLD || info.velocity.x < -600
      const decideUp =
        info.offset.y < -SWIPE_THRESHOLD || (info.velocity.y < -600 && info.offset.y < -30)
      if (decideRight) handleBucket('love', true)
      else if (decideLeft) handleBucket('skip', true)
      else if (decideUp) setNoteOpen(true)
    },
    [handleBucket, reviewEnabled]
  )

  const keyboardHandlers = useMemo<Record<string, () => void>>(() => {
    const handlers: Record<string, () => void> = {
      z: () => setZoomed((v) => !v),
      ' ': () => goNext(),
      Backspace: () => goPrev(),
      g: () => navigate('/gallery'),
    }
    if (reviewEnabled) {
      handlers.ArrowRight = () => handleBucket('love')
      handlers.ArrowLeft = () => handleBucket('skip')
      handlers.ArrowDown = () => handleBucket('maybe')
      handlers.ArrowUp = () => setNoteOpen(true)
      handlers.l = () => handleBucket('love')
      handlers.s = () => handleBucket('skip')
      handlers.m = () => handleBucket('maybe')
      handlers.n = () => setNoteOpen(true)
    }
    return handlers
  }, [handleBucket, goNext, goPrev, navigate, reviewEnabled])
  useKeyboard(keyboardHandlers)

  const categoryPosition = useMemo(() => {
    if (!photo) return { count: 0, idx: 0 }
    const sameCategory = photos.filter((p) => p.category === photo.category)
    const idxInCat = sameCategory.findIndex((p) => p.id === photo.id)
    return { count: sameCategory.length, idx: idxInCat + 1 }
  }, [photos, photo])

  if (!manifest) return null
  if (!photo) {
    return (
      <div className="flex flex-col min-h-[100dvh]">
        <div className="pt-9 md:pt-3" />
        <BrandBar title="Photo not found" back />
        <div className="flex-1 grid place-items-center px-6 text-center">
          <div className="max-w-sm">
            <p className="text-sm text-[var(--text-secondary)]">
              That photo isn't in this gallery. It may have been removed by the photographer.
            </p>
            <button
              onClick={() => navigate('/gallery')}
              className="mt-5 px-4 h-10 rounded-xl bg-[var(--accent-primary)] text-[#0a0a0a] text-sm font-semibold"
            >
              Back to gallery
            </button>
          </div>
        </div>
      </div>
    )
  }
  const pickLabel = photographerPickLabel(manifest.photographer.name)

  return (
    <div className="relative flex h-[100dvh] min-h-0 flex-col overflow-hidden">
      <div className="pt-9 md:pt-3" />
      {/* Two positions, and they are NOT duplicates — a review of a capture
          read them as one because that gallery had a single category, where
          they coincide. The header counts within the CATEGORY it names beside
          it; the chip counts across every photo in the link, and that
          full-array index is contract-tested: it is what proves a tile opened
          from a FILTERED view lands on the right photo. Collapsing them loses
          a fact and breaks that proof. */}
      <BrandBar
        title={photo.category}
        subtitle={`${categoryPosition.idx} of ${categoryPosition.count}`}
        back
        brandLogoUrl={manifest.photographer.brandLogoUrl}
        rightSlot={
          <div className="flex items-center gap-1.5">
            {/* Download · only when the photographer enabled it on this link.
             *  When a full-size tier is on, the control offers an Original vs
             *  Web choice; web-only links keep the single button. */}
            {policy && photo.downloadUrl && (
              <div data-testid="download-web">
                <PolicyDownloadMenu
                  policy={policy}
                  availability={
                    photo.downloadAvailability ??
                    (policy.policyVersion === 0
                      ? { web: true, largest: true, jpg: true, raw: true }
                      : {})
                  }
                  downloadUrl={photo.downloadUrl}
                  onDownload={(tier) => {
                    if (!shareToken) return
                    void trackShareEvent(shareToken, 'photo_downloaded', {
                      photoId: photo.id,
                      metadata: {
                        tier,
                        source: 'photo_viewer',
                      },
                    })
                  }}
                />
              </div>
            )}
            <button
              aria-label="Open grid"
              onClick={() => navigate('/gallery')}
              className="w-10 h-10 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-subtle)] grid place-items-center text-[var(--text-secondary)]"
            >
              <LayoutGrid size={16} strokeWidth={2} />
            </button>
          </div>
        }
      />

      <main
        data-testid="photo-viewer-stage"
        className="relative mx-4 mb-2 mt-3 min-h-0 min-w-0 flex-1 overflow-hidden"
      >
        <AnimatePresence mode="popLayout" initial={false} custom={exitDirRef}>
          <motion.div
            key={photo.id}
            className="absolute inset-0 cursor-grab active:cursor-grabbing"
            drag={zoomed || markupMode || !reviewEnabled ? false : true}
            dragDirectionLock
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            dragElastic={0.6}
            onDragEnd={onDragEnd}
            style={{ x, y, rotate }}
            custom={exitDirRef}
            variants={{
              enter: { scale: 0.96, opacity: 0 },
              center: { scale: 1, opacity: 1, x: 0, y: 0 },
              // The payoff frame: a swiped card flies off toward its verdict;
              // taps and keyboard keep the neutral fade.
              exit: (dir: { current: number }) =>
                dir?.current
                  ? { x: dir.current * 480, rotate: dir.current * 14, opacity: 0 }
                  : { scale: 0.96, opacity: 0 },
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.18 }}
            onDoubleClick={() => setZoomed((v) => !v)}
          >
            <PhotoSurface
              src={zoomed ? photo.zoomUrl : photo.displayUrl}
              alt=""
              rounded="rounded-3xl"
              className="h-full w-full"
              fit="contain"
              onImageLoad={({ naturalWidth, naturalHeight }) =>
                setNaturalSize({ w: naturalWidth, h: naturalHeight })
              }
            />
            {/* Markup overlay — drawn comment regions + draw-to-anchor. Only
                meaningful once we know the intrinsic size for the contain math;
                pass-through (no drawing) until then. */}
            {naturalSize && (
              <AnnotationLayer
                naturalWidth={naturalSize.w}
                naturalHeight={naturalSize.h}
                comments={photoComments}
                activeId={selectedCommentId}
                onSelectRegion={(id) => setSelectedCommentId((cur) => (cur === id ? null : id))}
                drawing={markupMode}
                onDrawComplete={handleDrawComplete}
              />
            )}
            <div className="absolute top-4 left-4 bg-black/55 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1.5 rounded-full flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
              {i + 1} / {photos.length}
            </div>
            {photo.isRecommended && (
              <div className="absolute top-4 right-4 bg-white/90 text-[#0a0a0a] text-[11px] font-semibold px-2.5 py-1.5 rounded-full flex items-center gap-1">
                <Star size={11} fill="currentColor" strokeWidth={0} />
                {pickLabel}
              </div>
            )}
            <BrandingBadge size="md" />
            {reviewEnabled && (
              <>
                <motion.div
                  style={{ opacity: skipOpacity }}
                  className="absolute top-1/2 left-6 -translate-y-1/2 text-[var(--accent-danger)] text-2xl font-bold uppercase tracking-widest border-4 border-[var(--accent-danger)] rounded-2xl px-4 py-2 -rotate-12 pointer-events-none"
                >
                  Reject
                </motion.div>
                <motion.div
                  style={{ opacity: loveOpacity }}
                  className="absolute top-1/2 right-6 -translate-y-1/2 text-[var(--accent-success)] text-2xl font-bold uppercase tracking-widest border-4 border-[var(--accent-success)] rounded-2xl px-4 py-2 rotate-12 pointer-events-none"
                >
                  Select
                </motion.div>
                <motion.div
                  style={{ opacity: noteOpacity }}
                  className="absolute top-8 left-1/2 -translate-x-1/2 text-white text-xl font-bold uppercase tracking-widest border-4 border-white/80 rounded-2xl px-4 py-2 pointer-events-none"
                >
                  Add note
                </motion.div>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {reviewEnabled && (
          <KeyboardHint
            hints={[
              { key: 'L', label: 'Select' },
              { key: 'M', label: 'Undecided' },
              { key: 'S', label: 'Reject' },
              { key: 'N', label: 'Add note' },
              { key: '↑↓←→', label: 'Navigate' },
              { key: 'G', label: 'Grid' },
            ]}
          />
        )}
      </main>

      {/* First-frame hint. Two things it must not do, both found on a real
          phone: name keys a touch device does not have, and sit under the
          absolutely-positioned "Add note" pill that BucketBar hangs above the
          bar (measured at 390x812 — the pill covered its left third). So the
          copy follows the input the reader actually has. The overlap is fixed
          in BucketBar itself — the pill now sits inside the bar's own padding
          rather than hanging over whatever is above it — so this line needs no
          clearance of its own. */}
      {reviewEnabled && Object.keys(picks).length === 0 && (
        <div className="shrink-0 truncate px-4 pb-2 text-center text-[11px] text-[var(--text-tertiary)]">
          <span className="hidden sm:inline">← reject · select → · ↑ note · </span>
          <span className="sm:hidden">tap to decide · </span>
          double-tap to zoom
        </div>
      )}

      <AnimatePresence>
        {undo && (
          <motion.button
            key="swipe-undo"
            type="button"
            data-testid="photo-swipe-undo"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-24 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/75 px-4 py-2 text-[12px] font-semibold text-white backdrop-blur-md"
            onClick={() => {
              if (undoTimer.current) clearTimeout(undoTimer.current)
              // Restore what the swipe replaced and bring the frame back.
              setBucket(undo.photoId, undo.bucket ?? 'maybe')
              const backIdx = photoIndexById[undo.photoId]
              setUndo(null)
              if (backIdx !== undefined) navigate(`/photo/${backIdx}`, { replace: true })
            }}
          >
            Undo swipe
          </motion.button>
        )}
      </AnimatePresence>

      {reviewEnabled && (
        <>
          {/* Asked at the FIRST pick, above the bar they just used — so the
              studio gets a name from anyone who actually decides something,
              not only from those who reach the Send step. */}
          <div className="px-3">
            <IdentityPrompt />
          </div>
          <BucketBar
            current={currentPick?.bucket}
            onPick={handleBucket}
            onNote={() => setNoteOpen(true)}
            hasNote={Boolean(currentPick?.note)}
          />
        </>
      )}

      <NoteSheet
        open={reviewEnabled && noteOpen}
        initialNote={currentPick?.note}
        onClose={() => setNoteOpen(false)}
        onSave={(text) => {
          if (!currentPick) setBucket(photo.id, 'love')
          setNote(photo.id, text)
        }}
      />

      {/* Comment + mark buttons — a standalone conversation with the
          photographer, separate from the private pick Note. Always available
          (not gated on review mode), so a delivery-only gallery can still leave
          feedback. "Mark" arms draw mode to anchor the next comment to a spot. */}
      {commentEnabled && (
        <div className="flex shrink-0 items-center justify-end gap-2 px-4 pb-2">
          <button
            type="button"
            data-testid="photo-mark-button"
            onClick={() => {
              setMarkupMode((v) => !v)
              setSelectedCommentId(null)
            }}
            aria-label={markupMode ? 'Cancel marking' : 'Mark a spot on the photo'}
            aria-pressed={markupMode}
            className="flex h-11 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-medium shadow-lg"
            style={{
              borderColor: markupMode ? 'var(--accent-primary)' : 'var(--border-default)',
              background: markupMode ? 'var(--accent-primary)' : 'var(--surface-secondary)',
              color: markupMode ? '#0a0a0a' : 'var(--text-secondary)',
            }}
          >
            <SquareDashedMousePointer className="h-4 w-4" />
            {markupMode ? 'Drag to mark' : 'Mark'}
          </button>
          <button
            type="button"
            data-testid="photo-comment-button"
            onClick={() => setCommentOpen(true)}
            aria-label={
              photoComments.length > 0 ? `Comments (${photoComments.length})` : 'Add a comment'
            }
            className="flex h-11 items-center gap-1.5 rounded-full border border-[var(--border-default)] bg-[var(--surface-secondary)] px-3.5 text-[13px] font-medium text-[var(--text-secondary)] shadow-lg"
          >
            <MessageSquare className="h-4 w-4" />
            {photoComments.length > 0 ? photoComments.length : 'Comment'}
          </button>
        </div>
      )}

      <CommentSheet
        open={commentEnabled && commentOpen}
        comments={photoComments}
        onClose={() => setCommentOpen(false)}
        onSubmit={handleSubmitComment}
        activeId={selectedCommentId}
        onSelect={setSelectedCommentId}
        pendingRegion={pendingRegion}
        onClearPendingRegion={() => setPendingRegion(null)}
      />
    </div>
  )
}
